import type { AccountInfo, AuthenticationResult, Configuration, SilentRequest } from '@azure/msal-browser'
import type { ApplicationInsights } from '@microsoft/applicationinsights-web'
import type { Ref, WritableComputedRef } from 'vue'

import { computed, inject, ref } from 'vue'
import { createGlobalState } from '@vueuse/core'
import { EventType, PublicClientApplication } from '@azure/msal-browser'

import { createInsights } from './insights'
import type { UserModule } from '~/types'
import mockedAccount from '~/mocks/account'
import { Role } from '~/types'
import { isOfflineDevelopment } from '~/utils'

export const createMsal = (options: Configuration) => ref<PublicClientApplication>(new PublicClientApplication(options))

export const apiTokenRequest: SilentRequest = {
  scopes: [import.meta.env.VITE_APP_API_SCOPE],
}

export const loginSettings = {
  scopes: [import.meta.env.VITE_APP_API_SCOPE],
  prompt: 'select_account',
}

export const silentTokenRequest: SilentRequest = {
  ...loginSettings,
  prompt: 'none',
  redirectUri: `https://${window.location.host}/_blank.html`,
}

export const MSAL_INSTANCE_KEY = Symbol('MSAL')

export const install: UserModule = async (app) => {
  if (import.meta.env.VITEST)
    return

  const instance = createMsal({
    auth: {
      clientId: import.meta.env.VITE_APP_ID,
      authority: `https://login.microsoftonline.com/${import.meta.env.VITE_APP_TENANT_ID}`,
      postLogoutRedirectUri: `https://${window.location.host}/sign-out`,
      redirectUri: `https://${window.location.host}/`,
    },
    system: {
      iframeHashTimeout: 10000,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true,
    },
  })

  await instance.value.initialize()

  const account = ref<AccountInfo | null>(isOfflineDevelopment ? mockedAccount : instance.value.getActiveAccount())

  const appInsights = createInsights()

  const setAccount = (val: AccountInfo | null) => {
    instance.value.setActiveAccount(isOfflineDevelopment ? mockedAccount : val)
    account.value = isOfflineDevelopment ? mockedAccount : val
    if (!val)
      return
    appInsights?.setAuthenticatedUserContext(
      val.username,
      val.localAccountId,
      true,
    )
  }

  instance.value.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload && typeof event.payload === 'object')
      setAccount((event.payload as AuthenticationResult).account)
  });

  (async () => {
    if (window.location.pathname !== '/sign-out')

      await instance.value.handleRedirectPromise()

    if (!instance.value.getAllAccounts().length) {
      instance.value.loginRedirect({
        ...loginSettings,
        state: window.location.pathname,
      })
    }
    else {
      setAccount(instance.value.getAllAccounts()[0])
    }
  })()

  app.provide(MSAL_INSTANCE_KEY, { instance, account, appInsights })
}

export const useMsal = createGlobalState(() => {
  const injected = inject<{ instance: Ref<PublicClientApplication>; account: WritableComputedRef<AccountInfo | null>; appInsights: ApplicationInsights }>(MSAL_INSTANCE_KEY)

  if (!injected)
    throw new Error('useMsal() cannot be called outside the setup() function of a component')

  const { instance, account, appInsights } = injected

  const roles = computed(() => (account.value) ? (account.value.idTokenClaims?.roles as Role[] || []) : [])

  const isAuthorized = computed(() => roles.value.includes(Role.Handler))

  const isSignedIn = computed(() => account.value !== null)

  const avatarInitials = computed(() => account.value?.name?.split(' ').map(i => i.charAt(0)).join('') ?? '')

  // TODO: add Avatar URL to MSAL account token
  const avatarUrl = computed(() => typeof account.value?.idTokenClaims?.picture === 'string' ? account.value?.idTokenClaims?.picture : '')

  const signOut = () => {
    instance.value.logoutRedirect()
  }

  const acquireApiToken = async () => {
    try {
      return await instance.value.acquireTokenSilent(apiTokenRequest)
    }
    catch {
      // The silent token acquisition failed, so the user may need to authenticate again.
      try {
        await instance.value.loginRedirect(loginSettings)
        return await instance.value.acquireTokenSilent(apiTokenRequest)
      }
      catch (err) {
        return { accessToken: '' }
      }
    }
  }

  return { instance, appInsights, account, acquireApiToken, isSignedIn, isAuthorized, avatarInitials, avatarUrl, signOut, roles }
})

export default { install }
